import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import Icon from '@brighthr/component-icon';

export type ContextMenuListItemProps = React.HTMLAttributes<HTMLElement> &
	React.ButtonHTMLAttributes<HTMLButtonElement> &
	React.AnchorHTMLAttributes<HTMLAnchorElement> & {
		text: string;
		href?: string;
		description?: string;
		iconName?: string;
		button?: boolean;
		destructive?: boolean;
	};

export const ContextMenuListItem = ({
	text,
	href,
	description,
	iconName,
	button = false,
	destructive = false,
	...props
}: ContextMenuListItemProps) => {
	if (button) {
		return (
			<li>
				<button
					type="button"
					className={cn(
						'flex items-center w-full px-3.5 py-1.5 text-left border-2 border-transparent outline-none cursor-pointer group/listitem disabled:cursor-not-allowed',
						destructive
							? 'hover:bg-error-50 focus:border-error-700 active:bg-error-700 disabled:bg-white'
							: 'hover:bg-primary-50 focus:border-primary-700 active:bg-primary-700 disabled:bg-white'
					)}
					{...props}
				>
					{iconName && (
						<span aria-hidden className="mr-3">
							<Icon
								iconName={iconName}
								className={
									destructive
										? 'fill-error-700 group-hover/listitem:fill-error-800 group-active/listitem:fill-white group-disabled/listitem:fill-link-disabled'
										: 'fill-primary-400 group-active/listitem:fill-white group-disabled/listitem:fill-link-disabled'
								}
							/>
						</span>
					)}
					<div className="flex flex-col">
						<span
							className={cn(
								'text-sm font-semibold',
								destructive
									? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white group-disabled/listitem:text-link-disabled'
									: 'text-primary-700 group-active/listitem:text-white group-disabled/listitem:text-link-disabled'
							)}
						>
							{text}
						</span>
						{description && (
							<span
								className={cn(
									'mt-1 text-xs font-normal',
									destructive
										? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white group-disabled/listitem:text-link-disabled'
										: 'text-neutral-600 group-active/listitem:text-white group-disabled/listitem:text-link-disabled'
								)}
							>
								{description}
							</span>
						)}
					</div>
				</button>
			</li>
		);
	}
	if (href) {
		return (
			<li>
				<a
					href={href}
					className={cn(
						'flex items-center w-full px-3.5 py-1.5 text-left border-2 border-transparent outline-none cursor-pointer group/listitem',
						destructive
							? 'hover:bg-error-50 focus:border-error-700 active:bg-error-700'
							: 'hover:bg-primary-50 focus:border-primary-700 active:bg-primary-700'
					)}
					{...props}
				>
					{iconName && (
						<span aria-hidden className="mr-3">
							<Icon
								iconName={iconName}
								className={
									destructive
										? 'fill-error-700 group-hover/listitem:fill-error-800 group-active/listitem:fill-white'
										: 'fill-primary-400 group-active/listitem:fill-white'
								}
							/>
						</span>
					)}
					<div className="flex flex-col">
						<span
							className={cn(
								'text-sm font-semibold',
								destructive
									? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white'
									: 'text-primary-700 group-active/listitem:text-white'
							)}
						>
							{text}
						</span>
						{description && (
							<span
								className={cn(
									'mt-1 text-xs font-normal',
									destructive
										? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white'
										: 'text-neutral-600 group-active/listitem:text-white'
								)}
							>
								{description}
							</span>
						)}
					</div>
				</a>
			</li>
		);
	}
	return (
		<li>
			<div
				className={cn(
					'flex items-center w-full px-3.5 py-1.5 text-left border-2 border-transparent outline-none cursor-pointer group/listitem',
					destructive
						? 'hover:bg-error-50 focus:border-error-700 active:bg-error-700'
						: 'hover:bg-primary-50 focus:border-primary-700 active:bg-primary-700'
				)}
				{...props}
			>
				{iconName && (
					<span aria-hidden className="mr-3">
						<Icon
							iconName={iconName}
							className={
								destructive
									? 'fill-error-700 group-hover/listitem:fill-error-800 group-active/listitem:fill-white'
									: 'fill-primary-400 group-active/listitem:fill-white'
							}
						/>
					</span>
				)}
				<div className="flex flex-col">
					<span
						className={cn(
							'text-sm font-semibold',
							destructive
								? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white'
								: 'text-primary-700 group-active/listitem:text-white'
						)}
					>
						{text}
					</span>
					{description && (
						<span
							className={cn(
								'mt-1 text-xs font-normal',
								destructive
									? 'text-error-700 group-hover/listitem:text-error-800 group-active/listitem:text-white'
									: 'text-neutral-600 group-active/listitem:text-white'
							)}
						>
							{description}
						</span>
					)}
				</div>
			</div>
		</li>
	);
};

export type ContextMenuNavigationProps = {
	ariaLabel: string;
	handleClose: () => void;
	menuPosition: ContextMenuNavigationPosition;
	children: React.ReactNode;
	triggerRef: React.RefObject<HTMLButtonElement>;
};

const ContextMenuNavigation = ({
	ariaLabel,
	handleClose,
	menuPosition,
	children,
	triggerRef,
}: ContextMenuNavigationProps) => {
	const navigationRef = useRef<HTMLElement>(null);

	const handleEscapeKey = (event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			handleClose();
		}
	};

	const handleClick = (event: MouseEvent) => {
		if (
			triggerRef.current &&
			navigationRef.current &&
			!triggerRef.current.contains(event.target as Node)
		) {
			handleClose();
		}
	};

	useEffect(() => {
		document.addEventListener('keydown', handleEscapeKey, true);
		document.addEventListener('click', handleClick);

		return () => {
			document.removeEventListener('keydown', handleEscapeKey, true);
			document.removeEventListener('click', handleClick);
		};
	});

	return (
		<nav
			ref={navigationRef}
			aria-label={ariaLabel}
			className={cn(
				'absolute top-full mt-3 bg-white z-50 min-w-[200px]',
				{
					'left-0': menuPosition === 'right',
					'left-[calc(50%-100px)]': menuPosition === 'center',
					'right-0': menuPosition === 'left',
				}
			)}
		>
			<ul className="relative flex flex-col overflow-hidden list-none bg-white border border-gray-300 rounded shadow-md">
				{children}
			</ul>
		</nav>
	);
};

type ContextMenuNavigationPosition = 'left' | 'center' | 'right';

export type ContextMenuProps = React.HTMLAttributes<HTMLDivElement> & {
	contextName: string;
	menuPosition?: ContextMenuNavigationPosition;
	children: React.ReactNode;
	customTriggerLayout?: React.ReactNode;
	onOpen?: () => void;
	text?: string;
};

export const ContextMenu = ({
	contextName,
	menuPosition,
	children,
	customTriggerLayout,
	onOpen,
	text,
	...rest
}: ContextMenuProps): React.ReactElement => {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [contextMenuOpen, setContextMenuOpen] = useState<boolean>(false);

	const handleClick = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		setContextMenuOpen((prevState) => !prevState);

		if (!contextMenuOpen && onOpen) {
			onOpen();
		}

		event.preventDefault();
	};

	const handleClose = () => {
		setContextMenuOpen(false);
	};

	const ariaLabel = `${
		contextMenuOpen ? 'Close' : 'Open'
	} ${contextName} menu`;

	return (
		<div className="flex justify-center" {...rest}>
			<div className="relative">
				<button
					ref={triggerRef}
					type="button"
					onClick={handleClick}
					title={ariaLabel}
					aria-label={ariaLabel}
					aria-expanded={contextMenuOpen}
					className="flex items-center group/trigger"
				>
					{customTriggerLayout || (
						<>
							{text && (
								<span className="mr-3 text-base font-bold text-primary-700 group-hover/trigger:text-primary-600 group-focus/trigger:text-primary-500">
									{text}
								</span>
							)}
							<span className="p-[3px] border bg-transparent border-transparent rounded-full group-hover/trigger:bg-primary-600 group-hover/trigger:border-primary-600 group-focus/trigger:bg-primary-500 group-focus/trigger:border-primary-600">
								<Icon
									iconName="menu-1-x-3-thick"
									className="fill-primary-700 group-hover/trigger:fill-white group-focus/trigger:fill-white"
									size="base"
								/>
							</span>
						</>
					)}
				</button>
				{contextMenuOpen && (
					<ContextMenuNavigation
						triggerRef={triggerRef}
						ariaLabel={contextName}
						menuPosition={menuPosition}
						handleClose={handleClose}
					>
						{children}
					</ContextMenuNavigation>
				)}
			</div>
		</div>
	);
};
